import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
 Users,
 Search,
 Edit,
 Trash2,
 UserPlus,
 Shield,
 Eye,
 AlertCircle,
 RefreshCw,
 CheckCircle,
 UserCheck
} from 'lucide-react'
import { Button, Card, CardHeader, CardTitle, CardContent, Input, ListPageSkeleton } from '../../components/ui'
import ConfirmDialog from '../../components/ui/ConfirmDialog'
import { useUsers } from '../../hooks/useUsers'

export const UsersPage: React.FC = () => {
 const navigate = useNavigate()
 const location = useLocation()
 const { users, isLoading, error, refetch, loadMore, hasMore, pagination, disableUser, activateUser } = useUsers()
 const [searchTerm, setSearchTerm] = useState('')
 const [filterRole, setFilterRole] = useState('all')
 const [filterStatus, setFilterStatus] = useState('all')
 const [confirmDialog, setConfirmDialog] = useState<{
 isOpen: boolean
 userId: string | null
 userName: string
 isLoading: boolean
 action: 'disable' | 'activate' | null
 }>({
 isOpen: false,
 userId: null,
 userName: '',
 isLoading: false,
 action: null
 })
 const [successMessage, setSuccessMessage] = useState('')

 // Mostra mensagem de sucesso se vier da navegação
 useEffect(() => {
 if (location.state?.successMessage) {
 setSuccessMessage(location.state.successMessage)
 // Limpa a mensagem após 5 segundos
 setTimeout(() => setSuccessMessage(''), 5000)
 // Limpa o state da navegação
 navigate(location.pathname, { replace: true })
 }
 }, [location.state, navigate, location.pathname])

 const getRoleBadgeColor = (roleName: string) => {
 switch (roleName?.toLowerCase()) {
 case 'owner':
 case 'proprietário':
 return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
 case 'manager':
 case 'gerente':
 return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
 case 'salesperson':
 case 'vendedor':
 return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
 case 'financial':
 case 'financeiro':
 return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
 case 'mechanic':
 case 'mecânico':
 return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
 case 'viewer':
 case 'visualizador':
 return 'bg-surface text-foreground'
 default:
 return 'bg-surface text-foreground'
 }
 }

 const getStatusBadgeColor = (status: string) => {
 switch (status) {
 case 'active':
 return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
 case 'inactive':
 return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
 default:
 return 'bg-surface text-foreground'
 }
 }

 const formatDate = (dateString: string) => {
 return new Date(dateString).toLocaleDateString('pt-BR', {
 day: '2-digit',
 month: '2-digit',
 year: 'numeric',
 hour: '2-digit',
 minute: '2-digit'
 })
 }

 const filteredUsers = users.filter(user => {
 const matchesSearch = (user.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
 (user.email?.toLowerCase().includes(searchTerm.toLowerCase()) || false)
 
 // Debug: Verificar a estrutura real dos dados (remover após teste)
 // console.log('User data:', user)
 
 // Tentar diferentes campos para o role
 const userRoleValue = user.role?.name || user.dealershipRole || (user.role as any)?.value
 
 // Mapear os valores do filtro para os valores reais das roles
 const roleMapping: Record<string, string> = {
 'Proprietário': 'owner',
 'Gerente': 'manager', 
 'Vendedor': 'salesperson',
 'Financeiro': 'financial',
 'Mecânico': 'mechanic',
 'Visualizador': 'viewer'
 }
 
 const matchesRole = filterRole === 'all' || 
 (userRoleValue && roleMapping[filterRole] === userRoleValue.toLowerCase())
 
 // Tentar diferentes campos para o status
 const userStatus = user.status || (user.isActive ? 'active' : 'inactive')
 const matchesStatus = filterStatus === 'all' || userStatus === filterStatus
 
 return matchesSearch && matchesRole && matchesStatus
 })

 const getRoleCount = (roleValue: string) => {
 // Mapear os valores do filtro para os valores reais das roles
 const roleMapping: Record<string, string> = {
 'Proprietário': 'owner',
 'Gerente': 'manager', 
 'Vendedor': 'salesperson',
 'Financeiro': 'financial',
 'Mecânico': 'mechanic',
 'Visualizador': 'viewer'
 }
 
 const actualRoleValue = roleMapping[roleValue]
 return users.filter(u => {
 const userRoleValue = u.role?.name || u.dealershipRole
 return userRoleValue && userRoleValue.toLowerCase() === actualRoleValue
 }).length
 }

 const getUserPhone = (user: any) => {
 if (!user.phones || user.phones.length === 0) return 'N/A'
 const primaryPhone = user.phones.find((phone: any) => phone.isPrimary) || user.phones[0]
 return `${primaryPhone.codeArea} (${primaryPhone.area}) ${primaryPhone.phone}`
 }

 const getRoleDisplayName = (roleValue: string) => {
 const roleDisplayMapping: Record<string, string> = {
 'owner': 'Proprietário',
 'manager': 'Gerente',
 'salesperson': 'Vendedor',
 'financial': 'Financeiro',
 'mechanic': 'Mecânico',
 'viewer': 'Visualizador'
 }
 return roleDisplayMapping[roleValue?.toLowerCase()] || roleValue || 'N/A'
 }

 const handleDisableUser = (userId: string, userName: string) => {
 setConfirmDialog({
 isOpen: true,
 userId,
 userName,
 isLoading: false,
 action: 'disable'
 })
 }

 const handleActivateUser = (userId: string, userName: string) => {
 setConfirmDialog({
 isOpen: true,
 userId,
 userName,
 isLoading: false,
 action: 'activate'
 })
 }

 const handleConfirmAction = async () => {
 if (!confirmDialog.userId || !confirmDialog.action) return

 setConfirmDialog(prev => ({ ...prev, isLoading: true }))

 try {
 if (confirmDialog.action === 'disable') {
 await disableUser(confirmDialog.userId)
 setSuccessMessage('Usuário inativado com sucesso!')
 } else if (confirmDialog.action === 'activate') {
 await activateUser(confirmDialog.userId)
 setSuccessMessage('Usuário ativado com sucesso!')
 }

 setConfirmDialog({
 isOpen: false,
 userId: null,
 userName: '',
 isLoading: false,
 action: null
 })
 } catch (error) {
 console.error(`Erro ao ${confirmDialog.action === 'disable' ? 'inativar' : 'ativar'} usuário:`, error)
 setConfirmDialog(prev => ({ ...prev, isLoading: false }))
 // Aqui você pode adicionar um toast de erro se tiver
 }
 }

 const handleCancelAction = () => {
 setConfirmDialog({
 isOpen: false,
 userId: null,
 userName: '',
 isLoading: false,
 action: null
 })
 }

 const handleEditUser = (userId: string) => {
 // Navegar diretamente para a página de perfil
 navigate(`/users/edit/${userId}`)
 }

 return (
 <div className="space-y-6">
 {/* Header */}
 <div className="flex items-center justify-between">
 <div>
 <h1 className="text-2xl font-bold text-foreground">
 Usuários
 </h1>
 <p className="text-muted-foreground">
 Gerencie os usuários da sua concessionária
 </p>
 </div>
 <Button 
 className="flex items-center"
 onClick={() => navigate('/users/new')}
 >
 <UserPlus className="h-4 w-4 mr-2" />
 Novo Usuário
 </Button>
 </div>

 {/* Stats Cards */}
 <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
 <Card>
 <CardContent className="p-6">
 <div className="flex items-center">
 <div className="p-2 bg-accent/10 dark:bg-primary-900 rounded-lg">
 <Users className="h-6 w-6 text-accent dark:text-accent" />
 </div>
 <div className="ml-4">
 <p className="text-sm font-medium text-muted-foreground">
 Total de Usuários
 </p>
 <p className="text-2xl font-bold text-foreground">
 {users.length}
 </p>
 </div>
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardContent className="p-6">
 <div className="flex items-center">
 <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
 <Shield className="h-6 w-6 text-green-600 dark:text-green-400" />
 </div>
 <div className="ml-4">
 <p className="text-sm font-medium text-muted-foreground">
 Proprietários
 </p>
 <p className="text-2xl font-bold text-foreground">
 {getRoleCount('Proprietário')}
 </p>
 </div>
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardContent className="p-6">
 <div className="flex items-center">
 <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
 <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
 </div>
 <div className="ml-4">
 <p className="text-sm font-medium text-muted-foreground">
 Gerentes
 </p>
 <p className="text-2xl font-bold text-foreground">
 {getRoleCount('Gerente')}
 </p>
 </div>
 </div>
 </CardContent>
 </Card>

 <Card>
 <CardContent className="p-6">
 <div className="flex items-center">
 <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
 <UserPlus className="h-6 w-6 text-orange-600 dark:text-orange-400" />
 </div>
 <div className="ml-4">
 <p className="text-sm font-medium text-muted-foreground">
 Vendedores
 </p>
 <p className="text-2xl font-bold text-foreground">
 {getRoleCount('Vendedor')}
 </p>
 </div>
 </div>
 </CardContent>
 </Card>
 </div>

 {/* Success Message */}
 {successMessage && (
 <Card>
 <CardContent className="p-6">
 <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
 <div className="flex items-center">
 <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-3 flex-shrink-0" />
 <p className="text-sm text-green-700 dark:text-green-300">
 {successMessage}
 </p>
 </div>
 </div>
 </CardContent>
 </Card>
 )}

 {/* Error State */}
 {error && (
 <Card>
 <CardContent className="p-6">
 <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
 <div className="flex items-center justify-between">
 <div className="flex items-center">
 <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mr-3 flex-shrink-0" />
 <div>
 <p className="text-sm text-red-700 dark:text-red-300">
 {error}
 </p>
 </div>
 </div>
 <Button
 variant="outline"
 size="sm"
 onClick={refetch}
 className="flex items-center"
 >
 <RefreshCw className="h-4 w-4 mr-2" />
 Tentar Novamente
 </Button>
 </div>
 </div>
 </CardContent>
 </Card>
 )}

 {/* Filters */}
 <Card>
 <CardContent className="p-6">
 <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
 <div className="relative">
 <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
 <Input
 placeholder="Buscar usuários..."
 value={searchTerm}
 onChange={(e) => setSearchTerm(e.target.value)}
 className="pl-10"
 />
 </div>
 <div>
 <select
 value={filterRole}
 onChange={(e) => setFilterRole(e.target.value)}
 className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
 >
 <option value="all">Todos os perfis</option>
 <option value="Proprietário">Proprietário</option>
 <option value="Gerente">Gerente</option>
 <option value="Vendedor">Vendedor</option>
 <option value="Financeiro">Financeiro</option>
 <option value="Mecânico">Mecânico</option>
 <option value="Visualizador">Visualizador</option>
 </select>
 </div>
 <div>
 <select
 value={filterStatus}
 onChange={(e) => setFilterStatus(e.target.value)}
 className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
 >
 <option value="all">Todos os status</option>
 <option value="active">Ativo</option>
 <option value="inactive">Inativo</option>
 </select>
 </div>
 </div>
 </CardContent>
 </Card>

 {/* Users Table */}
 <Card>
 <CardHeader>
 <div className="flex items-center justify-between">
 <CardTitle>Lista de Usuários</CardTitle>
 <div className="text-sm text-muted-foreground">
 <div>
 Mostrando {filteredUsers.length} de {users.length} usuários
 {pagination && (
 <span className="ml-2">
 (Total: {pagination.total})
 </span>
 )}
 </div>
 {(filterRole !== 'all' || filterStatus !== 'all' || searchTerm) && (
 <div className="mt-1 text-xs">
 Filtros: 
 {filterRole !== 'all' && <span className="ml-1">Perfil: {filterRole}</span>}
 {filterStatus !== 'all' && <span className="ml-1">Status: {filterStatus === 'active' ? 'Ativo' : 'Inativo'}</span>}
 {searchTerm && <span className="ml-1">Busca: "{searchTerm}"</span>}
 </div>
 )}
 </div>
 </div>
 </CardHeader>
 <CardContent>
 {isLoading ? (
 <ListPageSkeleton variant="list" rows={6} showFilters={false} />
 ) : (
 <div className="overflow-x-auto">
 <table className="w-full">
 <thead>
 <tr className="border-b border-border">
 <th className="text-left py-3 px-4 font-medium text-foreground">
 Usuário
 </th>
 <th className="text-left py-3 px-4 font-medium text-foreground">
 Telefone
 </th>
 <th className="text-left py-3 px-4 font-medium text-foreground">
 Perfil
 </th>
 <th className="text-left py-3 px-4 font-medium text-foreground">
 Status
 </th>
 <th className="text-left py-3 px-4 font-medium text-foreground">
 Último Login
 </th>
 <th className="text-left py-3 px-4 font-medium text-foreground">
 Ações
 </th>
 </tr>
 </thead>
 <tbody>
 {filteredUsers.map((user) => (
 <tr key={user.id} className="border-b border-border hover:bg-surface">
 <td className="py-4 px-4">
 <div className="flex items-center">
 <div className="h-10 w-10 bg-accent rounded-full flex items-center justify-center">
 <span className="text-white font-medium">
 {user.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
 </span>
 </div>
 <div className="ml-4">
 <p className="font-medium text-foreground">
 {user.name || 'Nome não informado'}
 </p>
 <p className="text-sm text-muted-foreground">
 {user.email || 'Email não informado'}
 </p>
 </div>
 </div>
 </td>
 <td className="py-4 px-4 text-sm text-muted-foreground">
 {getUserPhone(user)}
 </td>
 <td className="py-4 px-4">
 <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user.role?.name || user.dealershipRole || 'unknown')}`}>
 {getRoleDisplayName(user.role?.name || user.dealershipRole || '')}
 </span>
 </td>
 <td className="py-4 px-4">
 <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(user.status || (user.isActive ? 'active' : 'inactive'))}`}>
 {user.status === 'active' || user.isActive ? 'Ativo' : 'Inativo'}
 </span>
 </td>
 <td className="py-4 px-4 text-sm text-muted-foreground">
 {user.lastLogin ? formatDate(user.lastLogin) : 'Nunca'}
 </td>
 <td className="py-4 px-4">
 <div className="flex items-center space-x-2">
 <button className="p-2 text-muted-foreground hover:text-accent dark:hover:text-accent">
 <Eye className="h-4 w-4" />
 </button>
 <button 
 className="p-2 text-muted-foreground hover:text-accent dark:hover:text-accent transition-colors"
 title="Editar usuário"
 onClick={() => handleEditUser(user.id)}
 >
 <Edit className="h-4 w-4" />
 </button>
 {user.status === 'active' || user.isActive ? (
 <button 
 className="p-2 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors"
 title="Inativar usuário"
 onClick={() => handleDisableUser(user.id, user.name || 'Usuário')}
 >
 <Trash2 className="h-4 w-4" />
 </button>
 ) : (
 <button 
 className="p-2 text-muted-foreground hover:text-green-600 dark:hover:text-green-400 transition-colors"
 title="Ativar usuário"
 onClick={() => handleActivateUser(user.id, user.name || 'Usuário')}
 >
 <UserCheck className="h-4 w-4" />
 </button>
 )}
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>

 {filteredUsers.length === 0 && (
 <div className="text-center py-12">
 <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
 <p className="text-muted-foreground">
 Nenhum usuário encontrado
 </p>
 </div>
 )}
 </div>
 )}

 {/* Load More Button */}
 {hasMore && filteredUsers.length > 0 && (
 <div className="mt-6 text-center">
 <Button
 variant="outline"
 onClick={loadMore}
 disabled={isLoading}
 className="flex items-center"
 >
 {isLoading ? (
 <>
 <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent mr-2"></div>
 Carregando...
 </>
 ) : (
 <>
 <RefreshCw className="h-4 w-4 mr-2" />
 Carregar Mais Usuários
 </>
 )}
 </Button>
 {pagination && (
 <p className="text-sm text-muted-foreground mt-2">
 {pagination.total - users.length} usuários restantes
 </p>
 )}
 </div>
 )}
 </CardContent>
 </Card>

 {/* Dialog de Confirmação */}
 <ConfirmDialog
 isOpen={confirmDialog.isOpen}
 onClose={handleCancelAction}
 onConfirm={handleConfirmAction}
 title={confirmDialog.action === 'disable' ? 'Inativar Usuário' : 'Ativar Usuário'}
 description={
 confirmDialog.action === 'disable' 
 ? `Tem certeza que deseja inativar o usuário "${confirmDialog.userName}"? Esta ação pode ser revertida posteriormente.`
 : `Tem certeza que deseja ativar o usuário "${confirmDialog.userName}"? O usuário terá acesso ao sistema novamente.`
 }
 confirmText={confirmDialog.action === 'disable' ? 'Sim, Inativar' : 'Sim, Ativar'}
 cancelText="Cancelar"
 type={confirmDialog.action === 'disable' ? 'danger' : 'info'}
 isLoading={confirmDialog.isLoading}
 />
 </div>
 )
}
